import CGPLogger from './logger';
import Strings from './strings';
import _ from './underscore';

interface IStorage {
    set(key: string, value: any) : boolean;

    del(key: string): boolean;

    get(key: string, defaultValue: any): any | null;

    clear(): void;
};

const logger = new CGPLogger('Store');

class CGPStorage {
    /**
     * @property
     * @type {null|LocalStorage|WindowStorage}
     */
    _selectedStorage: IStorage = null;


    /**
     * CGP storage business logic place.
     */
    constructor() {
        this.init();
    }


    /**
     * Initialize the storage and initialize the currect instance of storage
     * Or from the window['localStorage'] or our window cgp storage ;).
     *
     * @returns {LocalStorage}
     */
    init() : void {
        if (this.isLocalStorageNameSupported()) {
            logger.debug('Selected to be main priority cgp storage from localstorage.');
            this._selectedStorage = new LocalStorage();
        } else {
            logger.debug('Selected to be main priority cgp storage from window unique localstorage.');
            this._selectedStorage = new WindowStorage();
        }
    }


    /**
     * Create cookie that shared/not between sub domains
     * @param name
     * @param value
     */
    setCookie(name: string, value: string) : void {
        let now = new Date();
        now.setFullYear(now.getFullYear() + 1);
        document.cookie = name + '=' + value + ';expires=' + now.toUTCString() + ';path=/';
    }

    /**
     * Get cookie by key
     * @param name
     * @returns {string}
     */
    getCookie(name: string): string {
        let match = document.cookie.match(new RegExp(name + '=([^;]+)'));
        if (match) {
            return match[1];
        }
        return '';
    }

    /**
     * Remove cookie by key
     * @param name
     */
    removeCookie(name: string) : void {
        document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/";
    }

    /**
     * Check wheter local storage supported.
     *
     * @returns {boolean}
     */
    isLocalStorageNameSupported(): boolean {
        let testKey = 'test';

        try
        {
            const storage: Storage = window.localStorage;
            storage.setItem(testKey, '1');
            storage.removeItem(testKey);
            return 'localStorage' in window && null !== window.localStorage;
        }
        catch (error)
        {
            logger.error('Local storage is not supported the user might running under incognito mode on safari yak!');
            return false;
        }
    }


    /**
     * Stores a value to the assigned key.
     *
     *
     * @param {string} key - the name of the key to be stored.
     * @param {*} value - could be anything, Array/String/Object.
     *
     * @returns {boolean} stating the save was successfully or not.
     * @public
     */
    set(key: string, value: any): boolean {
        return this._selectedStorage.set(key, value);
    }


    /**
     * Removes a key from the storage, also checks if exists.
     *
     *
     * @param {string} key - the name of the key to be deleted.
     * @returns {boolean} stating the delete was successfully or not.
     * @public
     */
     del(key: any): boolean {
        if(typeof key === 'string') {
            return this.delKey(key);
        } else {
            return this.delArray(key);
        }
     }

    /**
     * Removes a key from the storage, also checks if exists.
     *
     *
     * @param {string} key - the name of the key to be deleted.
     * @returns {boolean} stating the delete was successfully or not.
     * @public
     */
     delKey(key: string): boolean {
        return this._selectedStorage.del(key);
     }

    /**
     * remove array of keys from storage, also check if exists.
     * @param keys
     * @returns {boolean}  stating the delete was successfully or not
     */
     delArray(keys: string[]): boolean {
        let result = true;
        keys.forEach(function (key) {
            result = result && this._selectedStorage.del(key);
        }, this);

        return result;
     }

    /**
     * Get a specific key from the store. If is not set, optionally can set a default value
     *
     *
     * @param {string} key - the name of the key to be returned.
     * @param {*} defaultValue - if key is empty, will return the default
     *
     * @returns {*} the data is mixed, what-ever is stored in the cache will be returned.
     * @public
     */
    get(key: string, defaultValue?: any): any | null {
        return this._selectedStorage.get(key, defaultValue);
    }

    clearLocalStorage() {
        this._selectedStorage.clear();
    }
}

/**
 *
 */
class LocalStorage implements IStorage {
    /**
     * @property
     * @type {string}
     * @private
     */
    private readonly _perfix = 'cgp_';


    /**
     * Creates the store object and ensures it remains a Singleton.
     *
     * @constructor
     */
    constructor() {
        if (_.getQueryParameters()['clearstorage']) {
            this.clear();
        }
    }

    /**
     * Stores a value to the assigned key.
     *
     *
     * @param {string} key - the name of the key to be stored.
     * @param {*} value - could be anything, Array/String/Object.
     *
     * @returns {boolean} stating the save was successfully or not.
     * @public
     */
    set(key: string, value: any): boolean {
        if (!this.localStorageExists()) {
            return false;
        }

        try {
            localStorage.setItem(this.getKeyConvention(key), value);
        } catch (error) {
            throw error;
        }

        return true;
    }


    /**
     * Removes a key from the storage, also checks if exists.
     *
     *
     * @param {string} key - the name of the key to be deleted.
     * @returns {boolean} stating the delete was successfully or not.
     * @public
     */
    del(key: string): boolean {
        if (!this.localStorageExists() || null === this.get(key)) {
            return false;
        }

        localStorage.removeItem(this.getKeyConvention(key));
        return true;
    }


    /**
     * Get a specific key from the store. If is not set, optionally can set a default value
     *
     *
     * @param {string} key - the name of the key to be returned.
     * @param {*} defaultValue - if key is empty, will return the default
     *
     * @returns {*} the data is mixed, what-ever is stored in the cache will be returned.
     * @public
     */
    get(key: string, defaultValue?: any): any | null {
        if (!this.localStorageExists()) {
            return false;
        }

        key = this.getKeyConvention(key);

        return null === localStorage.getItem(key) && 'undefined' !== typeof defaultValue ? defaultValue : localStorage.getItem(key);
    }


    /**
     * Get all keys for the whole key convention
     *
     *
     *
     * @usage:
     *   // Defined already in localstorage
     *   localStorageKeys = [cgp_test1,test2,cgp_test3,test4]
     *
     *   getAll() {
     *      return [cgp_test1, cgp_test3];
     *   };
     *
     * @returns {*} Gets all the data stored in the cache.
     * @public
     */
    getAll() : any[] {
        let keys: any[] = [];

        if (!this.localStorageExists()) {
            return keys;
        }

        Object
            .keys(localStorage)
            .filter(key => this.isKeyConvention(key))
            .forEach((key) => {
                keys.push(key);
            });

        return keys;
    }


    /**
     * Verify localStore is defined and can be used in here :)
     *
     *
     * @returns {boolean} makes sure that localStorage exists and we are allowed to use it.
     * @public
     */
    localStorageExists() {
        try {
            return 'localStorage' in window && null !== window.localStorage;
        } catch (error) {
            logger.error(['localStorage not found, features related to store will be disabled for this session.']);
            return false;
        }
    }


    /**
     * Clear all the data from the localStorage, return true when the local is clear
     * and false when the localStorage not found in the global scope.
     *
     *
     *
     * @usage:
     *   clearLocalStorage() {
     *      // if the localStorage is defined in the global window.
     *      return true;
     *   };
     *
     * @public
     */
    clear() : void {
        if (!this.localStorageExists()) {
            return;
        }

        localStorage.clear();
    }


    /**
     * This function will check key start with cgp prefix.
     *
     *
     *
     * @usage:
     *   isKeyConvention('test') {
     *      return false;
     *   };
     *
     *   getKeyConvention('cgp_test') {
     *      return true;
     *   };
     *
     *
     * @param {string} key - that needs to be verified for convention.
     * @returns {boolean} if they key is valid.
     * @public
     */
    isKeyConvention(key: string) {
        let stringUtil = new Strings(key);
        if (stringUtil.startsWith(this._perfix)) {
            return true;
        }

        return false;
    }


    /**
     * Get string convention for localStorage key.
     * Extend the input key with the static prefix we define.
     *
     *
     *
     * @usage:
     *   getKeyConvention('test') {
     *      return cgp_test
     *   };
     *
     * @param {string} key - that needs to be converted to our convention.
     * @returns {string} of the key to be stored. With naming convention set.
     * @public
     */
    getKeyConvention(key: string) {
        if (!key) {
            return '';
        }

        // remove unwanted white spaces.
        let keyConvention = key.trim();
        let stringUtil = new Strings(keyConvention);
        if (!stringUtil.startsWith(this._perfix)) {
            return this._perfix + keyConvention;
        }

        return keyConvention;
    }
}

class WindowStorage implements IStorage {
    /**
     * @property
     * @type {string}
     * @private
     */
    private readonly _storgeKeyName = 'cgpStorage';


    /**
     * @property
     * @type {null|object}
     * @private
     */
    private _cgpStorage: {[key: string]: any} = null;


    /**
     * Creates the store object and ensures it remains a Singleton.
     *
     * @constructor
     */
    constructor() {
        if (_.getQueryParameters()['clearstorage']) {
            this.clear();
        }

        this.init();
    }


    /**
     * Initializing the CGP window local storage.
     */
    init() {
        // Define CGP storage on the global window.
        this._cgpStorage = (window as any)[this._storgeKeyName] = {};

        logger.debug('Defined a new window cgp local storage.');
        logger.debug('Finish initializing window storage.');
    }


    /**
     * Stores a value to the assigned key.
     *
     *
     * @param {string} key - the name of the key to be stored.
     * @param {*} value - could be anything, Array/String/Object.
     *
     * @returns {boolean} stating the save was successfully or not.
     * @public
     */
    set(key: string, value: any): boolean {
        if (!this.storageExists()) {
            return false;
        }

        try {
            this._cgpStorage[key] = value;
        } catch (error) {
            throw error;
        }

        return true;
    }


    /**
     * Removes a key from the storage, also checks if exists.
     *
     *
     * @param {string} key - the name of the key to be deleted.
     * @returns {boolean} stating the delete was successfully or not.
     * @public
     */
    del(key: string): boolean {
        if (!this.storageExists() || _.isUndefined(this.get(key))) {
            return false;
        }

        delete this._cgpStorage[key];
        return true;
    }


    /**
     * Get a specific key from the store. If is not set, optionally can set a default value
     *
     *
     * @param {string} key - the name of the key to be returned.
     * @param {*} defaultValue - if key is empty, will return the default
     *
     * @returns {*} the data is mixed, what-ever is stored in the cache will be returned.
     * @public
     */
    get(key: string, defaultValue?: any): any | null {
        if (!this.storageExists()) {
            return defaultValue;
        }

        const defaultGetter = _.isUndefined(defaultValue) ? undefined : defaultValue;
        return this._cgpStorage[key] || defaultGetter;
    }


    /**
     * Check if window cgp stroage is exist.
     *
     * @returns {boolean}
     */
    storageExists(): boolean {
        return !_.isNull(this._cgpStorage);
    }


    /**
     * Get all keys for the whole key convention
     *
     *
     *
     * @usage:
     *   // Defined already in localstorage
     *   localStorageKeys = [cgp_test1,test2,cgp_test3,test4]
     *
     *   getAll() {
     *      return [cgp_test1, cgp_test3];
     *   };
     *
     * @returns {*} Gets all the data stored in the cache.
     * @public
     */
    getAll(): any[] {
        let keys: any[] = [];

        if (!this.storageExists()) {
            return keys;
        }

        Object
            .keys(this._cgpStorage)
            .forEach((key) => {
                keys.push(key);
            });

        return keys;
    }


    /**
     * Clear all the data from the localStorage, return true when the local is clear
     * and false when the localStorage not found in the global scope.
     *
     *
     *
     * @usage:
     *   clear() {
     *   };
     *
     * @returns {boolean} stating the clear was successfully or not.
     * @public
     */
    clear() : void {
        (window as any)[this._storgeKeyName] = {};
    }
}


export default new CGPStorage();
